import GlobalStyles from './styled/globalStyles'

import * as React from 'react'

import { ApolloProvider } from '@apollo/client'
import { WebfontLoader } from '@chegg-ui/theme'
import configureStore, { history } from './configureStore'

import SimpleErrorBoundary from '@src/components/ErrorBoundary/SimpleErrorBoundary'
import Helmet from '@src/components/Helmet'
import OptimizelyProvider from '@src/components/OptimizelyProvider'
import Router from '@src/components/Router'
import site from '@src/state/site/site'
import apolloClient from '@src/utils/http-clients/graphApi'
import { Provider } from 'react-redux'
import { ThemeProvider } from 'styled-components'
import FeatureFlagsProvider from './FeatureFlagsProvider'
import Modals from './Modals'
import theme from './styled/theme'
import { useSetVersion } from './utils/hooks/useSetVersion'
import { Environment, init, listen } from '@chegg/one-auth-sdk'
import { LOCAL_OR_PLAYGROUND } from '@src/utils/localOrPlayground'
import { CITATION_MACHINE, EASYBIB, BIBME } from '@src/state/site/types'
import { tokenRequest } from './utils/http-clients/cfeAuthApi'
import { Env, getOneGraphQLClient } from '@chegg-plat-ui/utils'
import { useAuthenticationHook } from '@src/hooks/useAuthenticationHook'
import { useDeviceID } from '@src/hooks/useDeviceID'

export const store = configureStore({
  site,
})

const getCookieDomain = (): string => {
  switch (process.env.REACT_APP_SITE) {
    case EASYBIB:
      return '.easybib.com'
    case CITATION_MACHINE:
      return '.citationmachine.net'
    case BIBME:
      return '.bibme.org'
    default:
      return '.chegg.com'
  }
}
export const getOneAuthClientId = (): string => {
  switch (process.env.REACT_APP_SITE) {
    case EASYBIB:
      return 'EB'
    case CITATION_MACHINE:
      return 'CM'
    case BIBME:
      return 'BM'
    default:
      return 'CHGG'
  }
}

export const env = LOCAL_OR_PLAYGROUND ? Environment.test : Environment.prod
const AUTH_GATE_URL = `https://auth-gate${
  env === Environment.test ? '.playground' + getCookieDomain() : getCookieDomain()
}/auth-gate/graphql`
init({
  env,
  clientId: getOneAuthClientId() as any,
  domainUrl: getCookieDomain(),
  cookieDomain: getCookieDomain(),
  authGateUrl: AUTH_GATE_URL,
})

// Need this to ensure that school dropdown component in oneAuth uses correct graphql endpoint,
// as without this it would depend on CHEGG_ENV variable that we cannot set through react application
// and can only set REACT_APP_CHEGG_ENV
getOneGraphQLClient(process.env.REACT_APP_CHEGG_ENV as Env)

const tokenRefreshSuccessCallback = async (token: any) => {
  if (token) {
    await tokenRequest({ fromTokenRefresh: true })
  }
}
listen('tokens', tokenRefreshSuccessCallback)

const AppContent: React.FC = () => {
  useAuthenticationHook()
  useDeviceID()
  return (
    <>
      <Router history={history} />
      <Modals />
    </>
  )
}

const App: React.FunctionComponent<{}> = () => {
  useSetVersion()

  return (
    <SimpleErrorBoundary>
      <WebfontLoader />
      <GlobalStyles />
      <Provider store={store}>
        <ThemeProvider theme={theme as any}>
          <OptimizelyProvider>
            <FeatureFlagsProvider>
              <ApolloProvider client={apolloClient}>
                <Helmet />
                <AppContent />
              </ApolloProvider>
            </FeatureFlagsProvider>
          </OptimizelyProvider>
        </ThemeProvider>
      </Provider>
    </SimpleErrorBoundary>
  )
}

export default App



// WEBPACK FOOTER //
// ./src/App.tsx